<!--
  - ===========================================================================
  - [StrongGate Limited] [http://www.stronggate.com]
  - Copyright (c) 2023. All rights Reserved.
  - Unauthorized access, use, dissemination, distribution, reproduction of this
  - file, or a portion of, in any form is prohibited and unlawful. If you receive
  - this file, and you are not an authorized company employee, or you believe you
  - have received this file in error, please notify the sender (or originator)
  - and promptly delete this file.
  -
  - Project:
  - File: ztn-button.vue
  - Created by: ChanYing
  - Create Date: 2023-04-06 10:06
  - Version Number: 1.0
  - Version Description:
  -
  - =============================================================================
  -->

<template>
  <button
      class="ztn-button"
      :class="{ 'ztn-button--danger': variant === 'danger' }"
      type="button"
      :disabled="disabled"
      :name="name"
      :id="id"
  >
    <img class="ztn-button__icon" v-show="icon" :src="icon" alt style="float: left;"/>
    <img :src="prefixIcon" v-show="prefixIcon" alt class="ztn-button__icon"/>
    <span class="ztn-page-t400 ztn-button__text">
      {{ buttonText }}
      <slot></slot>
    </span>
    <img :src="suffixIcon" v-show="suffixIcon" alt class="ztn-button__icon"/>
  </button>
</template>

<script>
export default {
  name: "ztn-button",
  props: {
    icon: [Object, String],
    prefixIcon: [Object, String],
    suffixIcon: [Object, String],
    buttonText: String,
    isLoading: Boolean,
    disabled: Boolean,
    name: String,
    id: String,
    variant: {
      type: String,
      default: "primary",
      validator: (value) => ["primary", "danger"].includes(value)
    }
  },
}
</script>

<style scoped>
.ztn-button {
  position: relative;
  min-height: var(--control-height-md);
  box-sizing: border-box;
  align-items: center;
  border-radius: var(--radius-sm);
  border: 1px solid var(--base-primary-fill-edge, var(--line-box-border-1));
  background: var(--base-primary-fill);
  color: var(--on-primary-fill);
  padding: 0 var(--space-4);
  margin-right: var(--space-3);
}

/* Destructive is not accent-driven, so it reads the action button's component tokens
   (design system §7.1). Primary above stays on the role layer: on this console primary
   follows the accent the user chose, and the component token is the fixed brand. */
.ztn-button--danger {
  background: var(--ztn-action-button-box-destructive-fill);
  border-color: var(--ztn-action-button-box-destructive-edge, var(--ztn-action-button-box-destructive-fill));
  color: var(--ztn-action-button-label-destructive-text);
}

.ztn-button__icon {
  width: var(--icon-size-sm);
  height: var(--icon-size-sm);
}

.ztn-button:hover:not(:disabled) {
  box-sizing: border-box;
  align-items: center;
  background: var(--base-primary-fill-h);
  border: 1px solid var(--base-primary-fill-edge, var(--outline));
  border-radius: var(--radius-sm);
}

.ztn-button--danger:hover:not(:disabled) {
  background: var(--base-danger-fill-h);
  border-color: var(--base-danger-fill-edge, var(--base-danger-fill-h));
}

.ztn-button:disabled {
  background: transparent;
  border-color: transparent;
  color: var(--on-surface);
  cursor: not-allowed;
}

.ztn-button:disabled::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--on-surface);
  opacity: var(--state-disabled-container-opacity);
  pointer-events: none;
}

.ztn-button:disabled .ztn-button__text,
.ztn-button:disabled img {
  position: relative;
  z-index: 1;
  opacity: var(--state-disabled-content-opacity);
}

.ztn-button__text {
  vertical-align: middle;
  margin-left: var(--space-1);
  margin-right: var(--space-1);
  color: inherit;
}

</style>